import React, { FC, useEffect, useState } from 'react'
import Select, { MultiValue, SingleValue } from 'react-select'
import { Guid } from '../../../api/baseApi'
import { CampaignPeriod, CampaingListModel, createCampaign, CreateCampaignRequest, updateCampaign, UpdateCampaignRequest } from '../../../api/campaingsApi'
import { getSiteNestedPlayListsForCampaign, SiteNestedPlaylistForCampaign } from '../../../api/channelsApi'
import { Client, createClient, getClients } from '../../../api/clientApi'
import { getTags } from '../../../api/tagApi'
import { currentUserHasPermission, Permission } from '../../../api/authApi'
import { formatDate } from '../../../services/dateService'
import { Button } from '../Button'
import { FormInput, FormRow, FormSelect, SelectOption } from '../formRow/FormRow'
import GlobalSpinner from '../globalsSpinner/GlobalSpinner'
import IconButton from '../iconButton/IconButton'
import { RightModal } from '../sideModal/Modal'
import { sortByNameString } from '../../../services/common'
import CustomSelect from '../customSelect/CustomSelect'

interface ICampaignModal {
    campaign: CampaingListModel | undefined,
    showCampaignModal: boolean,
    closeCampaignModal: () => void,
    updateCampaignListFunction: ()=> Promise<void>
}

const CampaignModal:FC<ICampaignModal> = (props) => {

    let {
        campaign, 
        showCampaignModal, 
        closeCampaignModal,
        updateCampaignListFunction
    } = props;

    let isExternalOrganisationUser = currentUserHasPermission(Permission.ExternalOrganisationUser);
    
    let [isLoading, setIsLoading] = useState(false);
    let [modalTitle, setModalTitle] = useState("");
    let [campaignName, setCampaignName] = useState("");
    let [isCampaignNameError, setIsCampaignNameError] = useState(false);

    let [campaignId, setCampaignId] = useState("");

    let [clientToggle, setClientToggle] = useState(false);
    let [isSelectedClientError, setIsSelectedClientError] = useState(false);
    let [clients, setClients] = useState<Client[]>([]);
    let [selectedClient, setSelectedClient] = useState<SingleValue<Client>>(null);
    let [createClientName, setCreateClientName] = useState("");
    let [isClientNameError, setIsClientNameError] = useState(false);

    let [isPeriodStartError, setIsPeriodStartError] = useState(false);
    let [isPeriodEndError, setIsPeriodEndError] = useState(false);
    let [isPeriodError, setIsPeriodError] = useState(false);
    let [periods, setPeriods] = useState<CampaignPeriod[]>([]);
    let [periodStart, setPeriodStart] = useState("");
    let [periodEnd, setPeriodEnd] = useState("");

    let [siteNestedPlaylists, setSiteNestedPlaylists] = useState<SiteNestedPlaylistForCampaign[]>([])
    let [selectedSiteNestedPlaylists, setSelectedSiteNestedPlaylists] = useState<SiteNestedPlaylistForCampaign[]>([]);

    let [tags, setTags] = useState<SelectOption[]>([]);
    let [isTagError, setIsTagError] = useState(false);
    let [selectedTags, setSelectedTags] = useState<Guid[]>([]);

    useEffect(()=>{
        async function fetchData(){

            await Promise.all([updateClients(),updateTags(),updateNestedPlaylists()])

        }
        fetchData();
    },[isExternalOrganisationUser])

    useEffect(()=>{
        if (campaign === undefined){
            setModalTitle("Add Campaign");
            setCampaignName("");
            setCampaignId("")
            setSelectedClient(null)
            setPeriods([]);
            setSelectedSiteNestedPlaylists([]);
            setSelectedTags([]);
        } else {
            setModalTitle(`Edit Campaign ${campaign.name}`);
            setCampaignName(campaign.name);
            setCampaignId(campaign.campaignId ? campaign.campaignId: "")
            setSelectedClient(campaign.client);
            setPeriods([...campaign.periods]);
            setSelectedSiteNestedPlaylists(campaign.playLists.map(it => it));
            setSelectedTags(campaign.tags.map(it => it.id));
        }
    },[campaign])

    const updateClients = async () => {
        let getClientsResponse = await getClients();
        if( getClientsResponse.type === "succes") {
            setClients(getClientsResponse.data.clients);
        }
    }
    const updateTags = async () => {
        let getTagsResponse = await getTags();
        if( getTagsResponse.type === "succes"){
            setTags(getTagsResponse.data.items.map(it => { return {name: it.name, value: it.id}}));
        }
    }
    const updateNestedPlaylists = async () => {
        let getSiteNestedPlaylistResponse = await getSiteNestedPlayListsForCampaign();
        if(getSiteNestedPlaylistResponse.type === "succes") {
            setSiteNestedPlaylists(getSiteNestedPlaylistResponse.data.items)
        }
    }

    const setCampaignTitle = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsCampaignNameError(false);
        setCampaignName(event.target.value);
    }

    const setCampaignIdForCampaign = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCampaignId(event.target.value);
    }

    const setClientName = (event: React.ChangeEvent<HTMLInputElement>) => {
        setCreateClientName(event.target.value);
        setIsClientNameError(false);
    }

    const clientFormRowToggle = () => {
        setClientToggle(prev => !prev);
    }

    const createClientHandler = async () => {
        if(!!!createClientName){
            setIsClientNameError(true);
            return;
        }

        let result = await createClient({name : createClientName});
        if(result.type == "succes"){
            let getClientsResponse = await getClients();
            let curID = result.data.id;
            switch(getClientsResponse.type){
                case "succes" : {
                    let currentClient = getClientsResponse.data.clients.find(it => it.id === curID);
                    let t = currentClient ? currentClient : null;
                    setClients(getClientsResponse.data.clients);
                    setSelectedClient(t);
                    setCreateClientName("");
                }
            }
        }  

        clientFormRowToggle();     
    }

    const setStartDatePeriod = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsPeriodStartError(false);
        setPeriodStart(event.target.value);
    }

    const setEndDatePeriod = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsPeriodEndError(false);
        setIsPeriodError(false);
        setPeriodEnd(event.target.value);
    }

    const addPeriod = () => {
        if(!!!periodStart || !!!periodEnd){
            setIsPeriodStartError(true);
            setIsPeriodEndError(true);
            return;
        }

        setIsPeriodError(false);
        setPeriods([...periods, {startDate: periodStart, endDate: periodEnd}]);
    }

    const deletePeriod = (index: number) => {
        let newArr = periods;
        newArr.splice(index, 1);
        setPeriods([...newArr]);
    }

    const onSelectedNestedPlayListForCampaignChange = (newValue : MultiValue<SiteNestedPlaylistForCampaign> | SingleValue<SiteNestedPlaylistForCampaign>) => {
        if(newValue && Array.isArray(newValue)) {
            setSelectedSiteNestedPlaylists([...newValue])
        }
    }
    // const onSelectedNestedPlayListForCampaignChange = (update: readonly SiteNestedPlaylistForCampaign[]) => {
    //     setSelectedSiteNestedPlaylists([...update])
    // }

    // const addTag= (id: Guid) => {
    //     setSelectedTags([...selectedTags, id]);
    //     setIsTagError(false);
    // }

    // const removeTag = (id: Guid) => {
    //     setSelectedTags(selectedTags.filter(it => it !== id));
    // }

    const createOrUpdateCampaign = async () => {

        let isValid = true;
        if(!!!campaignName){
            setIsCampaignNameError(true);
            isValid = false;
        }
        if(!!!selectedClient){
            setIsSelectedClientError(true);
            isValid = false;
        }    

        if(!!!periods || periods.length == 0){
            setIsPeriodError(true);
            isValid = false;
        }
        
        if(!isValid) return;
        
        let request : CreateCampaignRequest = {
            clientId: selectedClient ? selectedClient.id : '',
            name: campaignName,
            campaignId: campaignId ? campaignId : null,
            periods: periods,
            playListIds: selectedSiteNestedPlaylists.map(it => it.playListId),
            tagIds: selectedTags
        } 

        if(campaign?.id){
            let updateRequest : UpdateCampaignRequest = {...request, id: campaign.id };
            await updateCampaign(updateRequest);
        } else {
            await createCampaign(request);
        }

        await updateCampaignListFunction();
        closeCampaignModal();
    }

    const closeModalResetInputsAndErrors = () => {
        setCampaignName("");
        setIsCampaignNameError(false)
        setCreateClientName("");
        setIsClientNameError(false);
        setSelectedClient(null);
        setIsSelectedClientError(false);
        setIsPeriodError(false);
        setSelectedTags([])
        closeCampaignModal();
    }

    const onSelectedClient = (client: SingleValue<Client>) => {
        setSelectedClient(client)
        setIsSelectedClientError(false);
    }

    const onSelectedTags = (newValue : MultiValue<SelectOption> | SingleValue<SelectOption>) => {
        if(newValue){
            if(Array.isArray(newValue)){
                setSelectedTags(newValue.map(it => it.value))
            }
        }
    }

    return (
        <>
            <RightModal 
                title={modalTitle} 
                show={showCampaignModal}
                onRequestClose={closeModalResetInputsAndErrors}
            >
                    <FormRow>
                        <FormInput 
                            id="campaign-name"
                            title="Campaign Name"
                            isError={isCampaignNameError}
                            errorMessage="Please enter campaign name"
                            onChange={setCampaignTitle}
                            value={campaignName}
                        />
                    </FormRow>

                    <FormRow>
                        <FormInput 
                            id="campaign-id"
                            title="Campaign ID"
                            isError={false}
                            errorMessage="Please enter Campaign ID"
                            onChange={setCampaignIdForCampaign}
                            value={campaignId}
                        />
                    </FormRow>
                    
                    <div className={`form-row-overflow ${clientToggle ? "active" : ""}`} style={{"paddingTop":"5px"}}>
                        <div className="row-tab-active">
                            <FormRow>
                                <label>Advertiser </label>
                                <Select
                                    className={`w-100 ${isSelectedClientError ? "error" : ""}`}
                                    classNamePrefix={`select`}
                                    options={clients.sort((a,b) => sortByNameString(a.name, b.name))}
                                    filterOption={(option, input) => option.label.toLowerCase().includes(input.toLowerCase())}
                                    getOptionLabel={(option) => `${option.name}`}
                                    getOptionValue={(option) => `${option.id}`}
                                    onChange={onSelectedClient}
                                    value={selectedClient}
                                />
                            </FormRow>
                            
                            <div className="form-right-button">
                                <IconButton tooltipText="Add advertiser" type="plus" tooltipPosition="left" roundedButton={true} onClick={clientFormRowToggle} />
                            </div> 
                        </div>
                        <div className="row-tab-hidden">
                            <FormRow>
                                <FormInput
                                    id="client-name"
                                    title="New Advertiser"
                                    isError={isClientNameError}
                                    errorMessage="Please enter Advertiser name"
                                    onChange={setClientName}
                                    value={createClientName}
                                />
                            </FormRow>
                            
                            <div className="form-btn-group">
                                <Button color="green" label="Add Advertiser" onClick={createClientHandler} />
                                <Button color="gray" label="Cancel" onClick={()=>{
                                    setCreateClientName("");
                                    setIsClientNameError(false);
                                    clientFormRowToggle()
                                }} />
                            </div>
                        </div>
                    </div>

                    <div className="form-row divider">
                        <div className="d-flex" style={{'marginBottom':'10px'}}>
                            <div className="form-row-double" style={{'maxWidth':'316px'}}>
                                <div className="">
                                    <label htmlFor="start">Starts</label>
                                    <input 
                                        type="date" 
                                        className={`form-control ${isPeriodStartError ? "error" : ""}`}  
                                        name="" 
                                        id="start" 
                                        onChange={setStartDatePeriod}
                                    />
                                    {isPeriodStartError && <span className="error">Please select start period</span>}
                                    {isPeriodError && <span className="error">Please add at least one period</span>}
                                </div>
                                <div className="">
                                    <label htmlFor="ends">Ends</label>
                                    <input 
                                        type="date" 
                                        className={`form-control ${isPeriodEndError ? "error" : ""}`}  
                                        name="" 
                                        id="ends" 
                                        onChange={setEndDatePeriod}
                                        min={periodStart}
                                        disabled={!!!periodStart}
                                    />
                                    {isPeriodEndError && <span className="error">Please select start period</span>}
                                </div>
                            </div>
                            <div className="form-right-button">
                                <IconButton tooltipText="Add period" type="plus" tooltipPosition="left" roundedButton={true} onClick={addPeriod}/>
                            </div>
                        </div>  

                        <div className="tags-block">
                            <div>
                                {!!periods && periods.map((it ,index) => 
                                <span className="tag">
                                    <span className="tag-text">{formatDate(it.startDate)} - {formatDate(it.endDate)} </span>
                                    <IconButton tooltipText="Delete period" type="cross" onClick={()=>{deletePeriod(index)}} />
                                </span>)}
                            </div>   
                        </div>
                    </div>
                    
                    <FormRow>
                        <label>Nested Playlists</label>
                        <Select 
                            options={siteNestedPlaylists.sort((a,b) => sortByNameString(a.siteName, b.siteName))}
                            isMulti
                            classNamePrefix={`select`}
                            filterOption={(option, input)=> option.label.toLowerCase().includes(input.toLowerCase())}
                            getOptionLabel={(option) => `${option.siteName} | ${option.playListName}`}
                            getOptionValue={(option)=> `${option.playListId}`}
                            value={selectedSiteNestedPlaylists}
                            onChange={onSelectedNestedPlayListForCampaignChange}
                        />
                    </FormRow>  

                    <FormRow>
                        <CustomSelect
                            isSort={true}
                            label='Tags'
                            options={tags}
                            isMulti
                            classNamePrefix={`select`}
                            filterOption={(option, input)=> option.label.toLowerCase().includes(input.toLowerCase())}
                            getOptionLabel={(option) => `${option.name}`}
                            getOptionValue={(option)=> `${option.value}`}
                            value={tags.filter(it => selectedTags.some(it2 => it2 === it.value))}
                            onChange={onSelectedTags}
                        />
                    </FormRow>
                    <div className="modal-btn-group">
                        <Button color="green" label="Save" onClick={createOrUpdateCampaign}/>
                        <Button color="gray" label="Cancel" onClick={closeModalResetInputsAndErrors}/>
                    </div>
            </RightModal> 
            <GlobalSpinner isShow={isLoading} />
        </>
    )
}

export default CampaignModal
